import React, { useEffect, useRef, useState } from 'react';
import * as BABYLON from '@babylonjs/core';
import { buildTruss } from '../Editor/TrussSystem';

/**
 * Lightweight interactive Babylon 3D hero for the landing page.
 *
 * Scene contents (kept intentionally tiny — total < 2k vertices + ~200 instances):
 *   - Dark studio floor with a cyan wireframe grid
 *   - Horizontal aluminium truss with uprights
 *   - 3 stage projectors on the truss, each emitting a soft volumetric beam cone
 *   - A back LED wall built from a thin-instanced plane (flickering cyan/fuchsia)
 *
 * Performance guards:
 *   - Renders at max 30 fps (engine.setHardwareScalingLevel + rAF throttle)
 *   - Auto-pauses when the canvas leaves the viewport (IntersectionObserver)
 *   - Pauses on visibilitychange
 *   - Subtle mouse-parallax (no kbd camera) — never captures page scroll
 *
 * Props:
 *   - className : passthrough for layout sizing
 *   - onReady   : optional callback fired once after first render
 */
const LandingHero3D = ({ className = '', onReady }) => {
  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const sceneRef = useRef(null);
  const rafRef = useRef(null);
  const visibleRef = useRef(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!canvasRef.current) return;

    let disposed = false;

    try {
      const engine = new BABYLON.Engine(canvasRef.current, true, {
        preserveDrawingBuffer: false,
        stencil: false,
        antialias: true,
        powerPreference: 'low-power'
      });
      engine.setHardwareScalingLevel(1.25); // render slightly under native for free perf
      engineRef.current = engine;

      const scene = new BABYLON.Scene(engine);
      scene.clearColor = new BABYLON.Color4(0.015, 0.02, 0.05, 1);
      scene.ambientColor = new BABYLON.Color3(0.15, 0.17, 0.22);
      scene.fogMode = BABYLON.Scene.FOGMODE_EXP2;
      scene.fogDensity = 0.008;
      scene.fogColor = new BABYLON.Color3(0.01, 0.015, 0.04);
      sceneRef.current = scene;

      /* ─── Camera — frame the stage from mid-house ─── */
      const camera = new BABYLON.ArcRotateCamera(
        'heroCam',
        Math.PI / 2,           // longitude: on +Z axis, looking toward -Z (stage)
        Math.PI / 2.4,         // small downward tilt
        24,
        new BABYLON.Vector3(0, 4.5, -6),
        scene
      );
      camera.minZ = 0.1;
      camera.lowerRadiusLimit = camera.upperRadiusLimit = 24;
      // Don't attach pointer controls; we drive parallax manually.
      scene.activeCamera = camera;

      /* ─── Lights ─── */
      const hemi = new BABYLON.HemisphericLight('hemi', new BABYLON.Vector3(0, 1, 0), scene);
      hemi.intensity = 0.55;
      hemi.groundColor = new BABYLON.Color3(0.03, 0.05, 0.08);

      const key = new BABYLON.DirectionalLight('key', new BABYLON.Vector3(-0.4, -1, 0.3), scene);
      key.intensity = 0.6;

      /* ─── Floor with cyan wireframe grid (baked from a shader-less approach) ─── */
      const floor = BABYLON.MeshBuilder.CreateGround('floor', { width: 40, height: 30, subdivisions: 1 }, scene);
      const floorMat = new BABYLON.StandardMaterial('floorMat', scene);
      floorMat.diffuseColor = new BABYLON.Color3(0.02, 0.025, 0.05);
      floorMat.specularColor = BABYLON.Color3.Black();
      floor.material = floorMat;
      floor.receiveShadows = false;

      // Grid lines drawn via LinesSystem (cheap, one draw call)
      const lines = [];
      for (let i = -15; i <= 15; i++) {
        lines.push([new BABYLON.Vector3(i, 0.01, -15), new BABYLON.Vector3(i, 0.01, 15)]);
        lines.push([new BABYLON.Vector3(-20, 0.01, i), new BABYLON.Vector3(20, 0.01, i)]);
      }
      const grid = BABYLON.MeshBuilder.CreateLineSystem('grid', { lines }, scene);
      grid.color = new BABYLON.Color3(0.1, 0.55, 0.75);
      grid.alpha = 0.25;

      /* ─── LED wall (thin-instanced tile grid) ─── */
      const tileSrc = BABYLON.MeshBuilder.CreatePlane('tile', { width: 0.45, height: 0.45, sideOrientation: BABYLON.Mesh.DOUBLESIDE }, scene);
      const tileMat = new BABYLON.StandardMaterial('tileMat', scene);
      tileMat.emissiveColor = new BABYLON.Color3(1, 1, 1);
      tileMat.diffuseColor = BABYLON.Color3.Black();
      tileMat.specularColor = BABYLON.Color3.Black();
      tileMat.disableLighting = true;
      tileMat.backFaceCulling = false;
      tileSrc.material = tileMat;

      const cols = 24, rows = 10;
      const tileInstances = [];
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const x = (c - (cols - 1) / 2) * 0.5;
          const y = 2.2 + r * 0.5;
          const z = -8;
          tileInstances.push({ x, y, z, phase: (c * 0.37 + r * 0.21) });
        }
      }
      const matrixBuffer = new Float32Array(16 * tileInstances.length);
      const colorBuffer = new Float32Array(4 * tileInstances.length);

      tileInstances.forEach((t, i) => {
        const m = BABYLON.Matrix.Translation(t.x, t.y, t.z);
        m.copyToArray(matrixBuffer, i * 16);
        colorBuffer[i * 4 + 0] = 0.1;
        colorBuffer[i * 4 + 1] = 0.5;
        colorBuffer[i * 4 + 2] = 0.8;
        colorBuffer[i * 4 + 3] = 1;
      });
      tileSrc.thinInstanceSetBuffer('matrix', matrixBuffer, 16, false);
      tileSrc.thinInstanceSetBuffer('color', colorBuffer, 4, false);

      // Bezel frame behind the LED wall
      const bezel = BABYLON.MeshBuilder.CreateBox('bezel', { width: cols * 0.5 + 0.6, height: rows * 0.5 + 0.6, depth: 0.15 }, scene);
      bezel.position = new BABYLON.Vector3(0, 2.2 + (rows - 1) * 0.5 / 2 + 0.25, -8.08);
      const bezMat = new BABYLON.StandardMaterial('bezMat', scene);
      bezMat.diffuseColor = new BABYLON.Color3(0.02, 0.02, 0.04);
      bezMat.specularColor = BABYLON.Color3.Black();
      bezel.material = bezMat;

      /* ─── Truss — REAL lattice goal-post using the app's own truss builder ─── */
      try {
        const trussConfig = {
          structureType: 'goalpost',
          profile: 'F34-290',              // industry-standard 290mm 4-chord truss
          goalpost: { heightLeft: 8, heightRight: 8, topWidth: 18 },
          color: '#a8acb4',                // brushed aluminium
          showBasePlates: true,
          bayInterval: 0.35
        };
        const trussRoot = buildTruss(trussConfig, 'heroTruss', scene);
        trussRoot.position = new BABYLON.Vector3(0, 0, -5);
      } catch (e) {
        // If TrussSystem initialisation fails (missing profile cache etc.),
        // fall back to a simple metal framework so the scene still reads.
        console.warn('Real truss builder failed — using fallback boxes:', e);
        const trussMat = new BABYLON.StandardMaterial('trussMat', scene);
        trussMat.diffuseColor = new BABYLON.Color3(0.55, 0.57, 0.62);
        trussMat.specularColor = new BABYLON.Color3(0.2, 0.2, 0.25);
        const mk = (w, h, d, x, y, z) => {
          const b = BABYLON.MeshBuilder.CreateBox('bar', { width: w, height: h, depth: d }, scene);
          b.position = new BABYLON.Vector3(x, y, z);
          b.material = trussMat;
          return b;
        };
        mk(18, 0.3, 0.3, 0, 8, -5);
        mk(0.3, 8, 0.3, -9, 4, -5);
        mk(0.3, 8, 0.3,  9, 4, -5);
      }

      /* ─── Realistic projector fixtures on the truss ─── */
      const projBodyMat = new BABYLON.PBRMaterial('projBodyMat', scene);
      projBodyMat.albedoColor = new BABYLON.Color3(0.06, 0.07, 0.09);
      projBodyMat.metallic = 0.35;
      projBodyMat.roughness = 0.55;

      const projClampMat = new BABYLON.PBRMaterial('projClampMat', scene);
      projClampMat.albedoColor = new BABYLON.Color3(0.25, 0.26, 0.3);
      projClampMat.metallic = 0.85;
      projClampMat.roughness = 0.3;

      const projLensRingMat = new BABYLON.PBRMaterial('projLensRingMat', scene);
      projLensRingMat.albedoColor = new BABYLON.Color3(0.4, 0.42, 0.46);
      projLensRingMat.metallic = 0.9;
      projLensRingMat.roughness = 0.2;

      const projSlots = [
        { x: -7, beamColor: new BABYLON.Color3(0.1, 0.85, 1.0) },
        { x:  0, beamColor: new BABYLON.Color3(0.96, 0.85, 0.36) },
        { x:  7, beamColor: new BABYLON.Color3(0.85, 0.27, 0.94) }
      ];

      projSlots.forEach((p, i) => {
        const anchor = new BABYLON.TransformNode(`proj_${i}`, scene);
        anchor.position = new BABYLON.Vector3(p.x, 7.0, -5);

        // Main projector body — slightly tapered, with subtle chamfer feel
        const body = BABYLON.MeshBuilder.CreateBox(
          `projBody_${i}`,
          { width: 1.5, height: 0.95, depth: 1.9 },
          scene
        );
        body.material = projBodyMat;
        body.parent = anchor;

        // Rear vent grille (thin slats as emissive strips)
        const ventMat = new BABYLON.StandardMaterial(`vent_${i}`, scene);
        ventMat.diffuseColor = new BABYLON.Color3(0.02, 0.02, 0.025);
        ventMat.specularColor = BABYLON.Color3.Black();
        for (let v = 0; v < 6; v++) {
          const slat = BABYLON.MeshBuilder.CreateBox(
            `projVent_${i}_${v}`,
            { width: 1.1, height: 0.08, depth: 0.05 },
            scene
          );
          slat.parent = anchor;
          slat.position = new BABYLON.Vector3(0, 0.35 - v * 0.14, 0.97);
          slat.material = ventMat;
        }

        // Lens barrel — big cylinder on the front
        const barrel = BABYLON.MeshBuilder.CreateCylinder(
          `projBarrel_${i}`,
          { height: 0.75, diameterTop: 0.55, diameterBottom: 0.75, tessellation: 24 },
          scene
        );
        barrel.rotation.x = Math.PI / 2;
        barrel.position = new BABYLON.Vector3(0, -0.03, -1.35);
        barrel.material = projBodyMat;
        barrel.parent = anchor;

        // Lens ring — metallic outer bezel
        const lensRing = BABYLON.MeshBuilder.CreateTorus(
          `projRing_${i}`,
          { diameter: 0.82, thickness: 0.08, tessellation: 24 },
          scene
        );
        lensRing.rotation.x = Math.PI / 2;
        lensRing.position = new BABYLON.Vector3(0, -0.03, -1.72);
        lensRing.material = projLensRingMat;
        lensRing.parent = anchor;

        // Lens glass — emissive disc so it reads as "ON"
        const lens = BABYLON.MeshBuilder.CreateDisc(
          `projLens_${i}`,
          { radius: 0.32, tessellation: 24 },
          scene
        );
        lens.position = new BABYLON.Vector3(0, -0.03, -1.73);
        const lensMat = new BABYLON.StandardMaterial(`lensMat_${i}`, scene);
        lensMat.emissiveColor = p.beamColor.scale(1.15);
        lensMat.diffuseColor = BABYLON.Color3.Black();
        lensMat.disableLighting = true;
        lens.material = lensMat;
        lens.parent = anchor;

        // Rigging clamp — half-moon yoke on top hooking to the truss chord
        const clampBase = BABYLON.MeshBuilder.CreateBox(
          `clampBase_${i}`,
          { width: 0.3, height: 0.08, depth: 0.8 },
          scene
        );
        clampBase.position = new BABYLON.Vector3(0, 0.52, 0);
        clampBase.material = projClampMat;
        clampBase.parent = anchor;

        const clampArm = BABYLON.MeshBuilder.CreateTorus(
          `clampArm_${i}`,
          { diameter: 0.45, thickness: 0.06, tessellation: 20 },
          scene
        );
        clampArm.position = new BABYLON.Vector3(0, 0.78, 0);
        clampArm.rotation.y = Math.PI / 2;
        clampArm.material = projClampMat;
        clampArm.parent = anchor;

        /* Volumetric beam — narrow at lens, flaring onto the LED wall */
        const from = new BABYLON.Vector3(p.x, 7.0, -6.7);
        const to   = new BABYLON.Vector3(p.x, 4.0, -8);
        const delta = to.subtract(from);
        const dist = delta.length();
        const dir = delta.normalize();

        const beam = BABYLON.MeshBuilder.CreateCylinder(
          `beam_${i}`,
          { height: dist + 5, diameterTop: 0.28, diameterBottom: 5.2, tessellation: 4 },
          scene
        );
        const up = new BABYLON.Vector3(0, 1, 0);
        const q = BABYLON.Quaternion.FromUnitVectorsToRef(up, dir.scale(-1), new BABYLON.Quaternion());
        beam.rotationQuaternion = q;
        const mid = from.add(dir.scale((dist + 5) / 2));
        beam.position = mid;

        const beamMat = new BABYLON.StandardMaterial(`beamMat_${i}`, scene);
        beamMat.emissiveColor = p.beamColor;
        beamMat.diffuseColor = BABYLON.Color3.Black();
        beamMat.specularColor = BABYLON.Color3.Black();
        beamMat.alpha = 0.17;
        beamMat.disableLighting = true;
        beamMat.backFaceCulling = false;
        beam.material = beamMat;
        beam.alphaIndex = 10 + i;
      });

      /* ─── Animation: tile flicker + slow camera orbit ─── */
      let t0 = performance.now();
      let parallaxX = 0, parallaxY = 0;
      let targetPX = 0, targetPY = 0;
      let lastRender = 0;
      const FRAME_INTERVAL = 1000 / 30; // 30 fps cap

      const onPointerMove = (e) => {
        if (!canvasRef.current) return;
        const rect = canvasRef.current.getBoundingClientRect();
        const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        const ny = ((e.clientY - rect.top) / rect.height) * 2 - 1;
        targetPX = nx * 0.15;
        targetPY = -ny * 0.08;
      };
      window.addEventListener('pointermove', onPointerMove, { passive: true });

      const renderLoop = (now) => {
        if (disposed) return;
        rafRef.current = requestAnimationFrame(renderLoop);
        if (!visibleRef.current) return;
        if (now - lastRender < FRAME_INTERVAL) return;
        lastRender = now;

        const t = (now - t0) / 1000;

        // Tile flicker — cheap color buffer update
        for (let i = 0; i < tileInstances.length; i++) {
          const ti = tileInstances[i];
          const wave = Math.sin(t * 1.8 + ti.phase * 3.2);
          const pulse = 0.55 + 0.45 * wave;
          // Mix cyan (0.1, 0.8, 1.0) and fuchsia (0.85, 0.27, 0.94) by column
          const mix = (ti.x + 6) / 12; // 0..1 roughly
          colorBuffer[i * 4 + 0] = (0.1 + mix * 0.75) * pulse;
          colorBuffer[i * 4 + 1] = (0.8 - mix * 0.53) * pulse;
          colorBuffer[i * 4 + 2] = (1.0 - mix * 0.06) * pulse;
          colorBuffer[i * 4 + 3] = 1;
        }
        tileSrc.thinInstanceBufferUpdated('color');

        // Smooth parallax
        parallaxX += (targetPX - parallaxX) * 0.05;
        parallaxY += (targetPY - parallaxY) * 0.05;

        // Slow auto orbit + parallax offset
        camera.alpha = Math.PI / 2 + Math.sin(t * 0.18) * 0.18 + parallaxX;
        camera.beta  = Math.PI / 2.4 + parallaxY;

        scene.render();
      };
      rafRef.current = requestAnimationFrame(renderLoop);

      // Pause when canvas off-screen
      const io = new IntersectionObserver((entries) => {
        entries.forEach(e => { visibleRef.current = e.isIntersecting; });
      }, { threshold: 0.05 });
      io.observe(canvasRef.current);

      const onResize = () => engine.resize();
      window.addEventListener('resize', onResize);

      const onVisibility = () => { visibleRef.current = !document.hidden; };
      document.addEventListener('visibilitychange', onVisibility);

      if (onReady) onReady();

      return () => {
        disposed = true;
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('resize', onResize);
        document.removeEventListener('visibilitychange', onVisibility);
        io.disconnect();
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        try { scene.dispose(); } catch (e) { /* noop */ }
        try { engine.dispose(); } catch (e) { /* noop */ }
      };
    } catch (err) {
      console.warn('Landing hero 3D failed, falling back to CSS:', err);
      setFailed(true);
    }
  }, [onReady]);

  if (failed) return null;

  return (
    <canvas
      ref={canvasRef}
      className={className}
      data-testid="landing-hero-3d-canvas"
      style={{ display: 'block', width: '100%', height: '100%', outline: 'none' }}
    />
  );
};

export default LandingHero3D;
